<template>
    <div
        v-if="show"
        class="fixed inset-0 z-50 flex items-center justify-center"
    >
        <div
            class="absolute inset-0 bg-slate-900/50"
            @click="$emit('update:show', false)"
        ></div>
        <div
            class="relative bg-white dark:bg-slate-800 rounded-lg shadow-lg w-full max-w-sm md:max-w-md mx-4 p-6"
        >
            <div class="flex items-center justify-between mb-4">
                <h3 class="text-xl font-medium text-slate-900 dark:text-slate-100">
                    Reactivate subscription
                </h3>
                <button
                    class="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700 transition-colors"
                    @click="$emit('update:show', false)"
                >
                    <Icon icon="close" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                </button>
            </div>
            <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">
                You're reactivating your subscription. Nothing will be
                charged right now. Your next payment will happen on your
                regular billing date.
            </p>

            <!-- Select existing payment method if multiple -->
            <div v-if="paymentMethods.length > 1" class="mb-6">
                <p class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-3">
                    Payment method
                </p>
                <div class="flex flex-col gap-2">
                    <label
                        v-for="method in paymentMethods"
                        :key="method.id"
                        class="flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors"
                        :class="selectedPaymentMethod === method.id ? 'border-orange bg-orange/5' : 'border-slate-200 dark:border-slate-700'"
                    >
                        <input
                            type="radio"
                            :value="method.id"
                            v-model="selectedPaymentMethod"
                            class="w-4 h-4 text-orange border-slate-300 dark:border-slate-600 focus:ring-orange"
                        />
                        <div class="flex items-center gap-2">
                            <span
                                class="text-xs font-semibold uppercase bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded"
                            >
                                {{ method.card?.brand || 'Card' }}
                            </span>
                            <span class="text-sm text-slate-900 dark:text-slate-100">
                                ({{ method.card?.last4 }})
                            </span>
                            <span
                                v-if="method.id === defaultPaymentMethodId"
                                class="text-xs text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5"
                            >
                                Default
                            </span>
                        </div>
                    </label>
                </div>
            </div>

            <div class="flex justify-end gap-3">
                <button
                    class="px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-slate-100 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                    @click="$emit('update:show', false)"
                >
                    Cancel
                </button>
                <button
                    class="px-4 py-2.5 bg-10x-orange text-sm font-medium text-white rounded shadow-sm hover:bg-orange-600 transition-colors disabled:opacity-50"
                    :disabled="processing"
                    @click="handleReactivate"
                >
                    {{
                        processing ? 'Reactivating...' : 'Reactivate'
                    }}
                </button>
            </div>
            <div
                v-if="reactivateError"
                class="text-red-500 text-sm mt-3"
            >
                {{ reactivateError }}
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useTravelPlusStore } from '@/store/travelplus';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    paymentMethods: {
        type: Array,
        default: () => [],
    },
    defaultPaymentMethodId: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['update:show', 'reactivated']);

const travelPlusStore = useTravelPlusStore();
const selectedPaymentMethod = ref(null);
const processing = ref(false);
const reactivateError = ref('');

watch(
    () => props.show,
    (val) => {
        if (val) {
            selectedPaymentMethod.value =
                props.defaultPaymentMethodId || props.paymentMethods[0]?.id;
            reactivateError.value = '';
        }
    }
);

const handleReactivate = async () => {
    reactivateError.value = '';
    processing.value = true;
    try {
        await travelPlusStore.renewSubscription(selectedPaymentMethod.value);
        emit('update:show', false);
        emit('reactivated');
    } catch (e) {
        reactivateError.value =
            e?.response?.data?.message ||
            'Something went wrong. Please try again.';
    } finally {
        processing.value = false;
    }
};
</script>
